/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, Sorting, IForm, SortDirection } from 'app/shared/lib/essette-site';
import { IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { AssessmentService } from 'app/core/services/assessment-service';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EventAggregator } from 'aurelia-event-aggregator';
import { StoreFocusEvent, ReturnFocusEvent } from 'app/shared/lib/events';
import { UserTrackerContinueEvent } from 'app/core/lib/events';
import * as Core from 'app/core/models/essette-api';

interface IExternalAssessmentAccess {
    cci: boolean;
    iccc: boolean;
}

@autoinject()
export class AssessmentAttachments extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public targetId: string | null = null;
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public config: IAssessmentsConfig | null = null;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = true;
    @bindable
    public mcgCopyright: string | undefined = "";

    @bindable
    protected showIccc: boolean = false;

    protected attachments: ReadonlyArray<Core.AssessmentAttachment> = [];
    protected includeInactive: boolean = false;
    protected loadModal: boolean = false;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 20;

    protected externalAssessmentAccess: IExternalAssessmentAccess | null = null;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $form!: IForm;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly events: EventAggregator,
        private readonly service: AssessmentService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!this.targetId) { throw new Error('Bindable property [target-id] was not set'); }
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }
        if(!this.config.generateAttachmentRoute) { throw new Error('Bindable property [config.generateAttachmentRoute] was not set'); }

        this.service.configure(this.config);

        this.loadAttachments();
    }


    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected handleLinkClickCapture(): boolean {
        this.events.publish(new StoreFocusEvent());
        this.events.publish(new UserTrackerContinueEvent());
        return true;
    }

    ////****************** Functions ************************************************////
    protected getAttachmentRoute(attachmentId: string, task?: Core.Task, externalSystemCode?: string): string | null {
        if(!!this.targetId && !!this.config?.generateAttachmentRoute) {
            return this.config.generateAttachmentRoute(this.targetId, attachmentId, task, externalSystemCode);
        }

        throw new Error('Unable to generate attachment route');
    }

    public async reload(): Promise<void> {
        await this.loadAttachments();
    }

    protected openModal(): void {
        this.loadModal = true;
        this.$modal.open();
    }
    protected closeModal(): void {
        this.resetModal();
        this.$modal.close();
    }
    protected resetModal(): void {
        if(this.$form && this.$form.reset) {
            this.$form.reset();
        }
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent): void {
        if(detail.changes && Array.isArray(detail.changes)) {
            const count = (detail.changes as []).length;
            if(count > 0) {
                this.toaster.standard.added('am:assessment.$', true, count);
            }
        } else {
            this.toaster.standard.added('am:assessment.$', true);
        }

        this.$modal.close();
        this.reload();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();

        this.externalAssessmentAccess = {
            cci: this.accountContext.hasRouteAccess('cci.assessment'),
            iccc: this.accountContext.hasRouteAccess('iccc.assessment')
        } as IExternalAssessmentAccess;
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadAttachments(): Promise<void> {
        if(!this.targetId) return;

        await this.checkPermissions();
        const options: Partial<IQueryLoadOptions> = {
            includeInactive: this.includeInactive,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes = [
            'assessment',
            'metadata.activeChanged.byUser',
            'metadata.modified.byUser',
            'lastSession',
            'lastSession.metadata.modified.byUser',
            'task'
        ];

        const fields = [
            'createdInPortal',
            'description',
            'isCompleted',
            'isRequired',
            'lastSession.metadata.modified.byUser.description',
            'lastSession.metadata.modified.timestamp',
            'metadata.modified.byUser.name.full',
            'metadata.modified.timestamp',
            'task.attachedTo.type.code',
        ];

        if(this.config?.moduleCode !== 'UM' && (this.externalAssessmentAccess?.cci || this.externalAssessmentAccess?.iccc)) {
            includes.push('assessment.externalSystem');
            fields.push('assessment.externalSystem.code');
        }

        if(this.includeInactive) {
            includes.push('metadata.activeChanged.byUser');
            fields.push('metadata.activeChanged.byUser.name.full', 'metadata.activeChanged.timestamp');
        }

        try {
            this.state.beginLoad();
            const query = this.service.createAttachmentsByTargetQuery(this.targetId, options, includes, undefined, fields);
            if(!this.includeInactive) {
                query.filterOr((f) => {
                    f.filterIsNull('task.id');
                    f.filterBoolean('task.isActive', true);
                });
            }
            const response = await this.service.get<Core.AssessmentAttachment>(query);
            this.attachments = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.events.publish(new ReturnFocusEvent());
            this.state.endLoad();
        }
    }

    @apiCall
    public async activateAttachment(attachmentId: string, isActive: boolean): Promise<void> {
        if(!isActive) {
            const result = await confirmation.showDeactivate('am:assessment.$');
            if(result.dialogResponse === confirmation.options.CANCEL) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateAttachment(attachmentId, isActive);
            isSuccessful = response.isSuccessful;
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('am:assessment.$', isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
